import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationService } from '../../../core/services/api/notification.service';
import type { Notification } from '../../../core/models/notification.model';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, ButtonModule, DrawerModule, TranslatePipe],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
})
export class NotificationBellComponent implements OnInit {
  private readonly notifService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly unreadCount = this.notifService.unreadCount;
  readonly hasError = this.notifService.hasError;

  readonly panelOpen = signal(false);
  readonly notifications = signal<Notification[]>([]);
  readonly loading = signal(false);
  readonly expandedIds = signal<ReadonlySet<string>>(new Set());

  private static readonly BODY_PREVIEW_CHARS = 120;

  ngOnInit(): void {
    this.notifService.refreshCount();
  }

  openPanel(): void {
    this.panelOpen.set(true);
    this.expandedIds.set(new Set());
    this.notifService.refreshCount();
    this.loadNotifications();
  }

  closePanel(): void {
    this.panelOpen.set(false);
    this.expandedIds.set(new Set());
  }

  private loadNotifications(): void {
    this.loading.set(true);
    this.notifService
      .listRaw({ status: 'all', limit: 20 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.notifications.set(res.data ?? []);
        this.loading.set(false);
      });
  }

  isExpanded(documentId: string): boolean {
    return this.expandedIds().has(documentId);
  }

  isBodyExpandable(body: string | null | undefined): boolean {
    const text = (body || '').trim();
    if (!text) {
      return false;
    }
    return text.length > NotificationBellComponent.BODY_PREVIEW_CHARS || text.includes('\n');
  }

  toggleBody(documentId: string, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.expandedIds.update(current => {
      const next = new Set(current);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }

  markRead(notif: Notification, event: Event): void {
    event.stopPropagation();
    if (notif.readAt) return;
    this.notifService
      .markRead(notif.documentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.notifications.update(list =>
          list.map(n => (n.documentId === notif.documentId ? { ...n, readAt: new Date().toISOString() } : n)),
        );
      });
  }

  markAllRead(): void {
    this.notifService
      .markAllRead()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const now = new Date().toISOString();
        this.notifications.update(list => list.map(n => ({ ...n, readAt: n.readAt ?? now })));
      });
  }

  navigateTo(notif: Notification): void {
    if (!notif.readAt) {
      this.markRead(notif, new Event('click'));
    }
    if (notif.deepLink) {
      window.location.assign(notif.deepLink);
    }
  }

  severityIcon(severity: string): string {
    const map: Record<string, string> = {
      info: 'pi pi-info-circle',
      success: 'pi pi-check-circle',
      warning: 'pi pi-exclamation-triangle',
      error: 'pi pi-times-circle',
    };
    return map[severity] ?? 'pi pi-bell';
  }

  severityClass(severity: string): string {
    const map: Record<string, string> = {
      info: 'notif-item--info',
      success: 'notif-item--success',
      warning: 'notif-item--warning',
      error: 'notif-item--error',
    };
    return map[severity] ?? '';
  }
}
